import { inject, Injectable } from '@angular/core'
import { FeathersService } from '../../feathers.service'
import { SmsMessage } from '../../../models/smsMessage'
import { Workgroup } from '../../../models/workgroup'
import { from, Observable, Subject } from 'rxjs'
import { first, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { PrivateMessageQueryType } from '../../../models/privateMessage'
import { UsersService } from '../users.service'
import { User } from '../../../models/user'

export type SmsQueryType = 'full' | 'paginatedList'

@Injectable({
  providedIn: 'root',
})
export class SmsService {
  private feathers = inject(FeathersService)
  private userService = inject(UsersService)

  private TAG = 'SmsService'

  unreadCountForCurrentUser$$ = new Subject<number>()
  unreadCountForCurrentUser$ = this.unreadCountForCurrentUser$$.pipe(shareReplay({ bufferSize: 1, refCount: true }))

  constructor() {
    this.updateUnreadCount()
  }

  send(sms: SmsMessage) {
    sms.contact = undefined
    sms.smsDirection = 'out'
    sms.smsStatus = 'pending'

    return this.feathers.createWithFactory(SmsMessage, sms)
  }

  getAllSms() {
    return this.feathers.find(SmsMessage).pipe(
      tap((messages: SmsMessage[]) => console.log('getAllSms', messages)),
      withLatestFrom(this.userService.getCurrentUser()),
      map(([messages, user]) => {
        return this.setReadByCurrentUser(messages, user)
      }),
    )
  }

  getLatestOutboundSmsForWorkgroupPaginated(
    workgroup: Workgroup,
    queryType: SmsQueryType = 'full',
    limit: number = 20,
    skip: number = 0,
  ): Observable<SmsMessage[]> {
    return this.feathers.find(SmsMessage, {
      queryType,
      $limit: limit,
      $skip: skip,
      $sort: {
        updatedAt: -1,
      },
      $params_workgroupId: workgroup?.id,
      $params_smsDirection: 'out',
    })
  }

  getLatestInboundSmsForWorkgroupPaginated(
    workgroup: Workgroup,
    queryType: SmsQueryType = 'full',
    limit: number = 20,
    skip: number = 0,
  ): Observable<SmsMessage[]> {
    return this.feathers
      .find(SmsMessage, {
        queryType,
        $limit: limit,
        $skip: skip,
        $sort: {
          updatedAt: -1,
        },
        $params_workgroupId: workgroup?.id,
        $params_smsDirection: 'in',
      })
      .pipe(
        withLatestFrom(this.userService.getCurrentUser()),
        map(([messages, user]) => {
          this.updateUnreadCount()
          return this.setReadByCurrentUser(messages, user)
        }),
      )
  }

  public getUnreadCountForCurrentUser() {
    return this.unreadCountForCurrentUser$
  }

  private updateUnreadCount() {
    console.log(`${this.TAG}#updateUnreadCount`)
    this.getUnreadCountUpdateForCurrentUser()
      .pipe(first())
      .subscribe((count) => this.unreadCountForCurrentUser$$.next(count))
  }

  private getUnreadCountUpdateForCurrentUser(): Observable<number> {
    console.log(this.TAG, 'getUnreadCountUpdateForCurrentUser')
    return this.userService.getCurrentUser().pipe(
      switchMap((user) =>
        from(
          this.feathers.count(SmsMessage.serviceName, {
            queryType: PrivateMessageQueryType.countUnread,
            // recipientId: user.id,
          }),
        ),
      ),
      tap((e) => console.log('unreadCountUpdate', e)),
    )
  }

  async markRead(message: SmsMessage) {
    const user = await this.userService.getCurrentUserPromise()
    if (message.isReadByUser(user)) return
    await this.feathers.create<any>('sms-read', { smsMessageId: message.id }).then((_) => {
      message.setReadByCurrentUser(true)
      this.updateUnreadCount()
    })
  }

  private setReadByCurrentUser(messages: SmsMessage[], user: User) {
    return messages.map((m) => m.setReadByCurrentUser(m.isReadByUser(user)))
  }

  async markAllAsRead() {
    return await this.feathers.postCommand('msg-sms-markAllRead', {
      currentUserId: (await this.userService.getCurrentUserPromise()).id,
    })
  }
}
